import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useMemo } from 'react';
import _ from 'lodash';
/**
  * @module prop-wire
  * @description decorate/wire Component such that its value(s) can be both controlled and uncontrolled
  * @example
// see [Dropdown]{@link https://git.gorilla-technology.com/gorilla/react-ui/blob/master/src/components/dropdown.js} which is already wired on *value*

//we can use it as controlled
<Dropdown
  list={[{value:'id1', text:'Name 1'},{value:'id2', text:'Name 2'}]}
  value={externallyControlledValue}
  onChange={myCallbackFn} />

// or uncontrolled
<Dropdown
  list={[{value:'id1', text:'Name 1'},{value:'id2', text:'Name 2'}]}
  defaultValue={internallyManagedValue} />
 */

/**
 * Decorate given Component with configured wires for individually defined props
 * @param {component} Component - which component to apply wiring to?
 * @param {object} [propsCfg] - key-object(config) paris of configs for all props we want to wire
 * @param {object} [propsCfg.key] - wire config for this prop
 * @param {string} [propsCfg.key.name='*key*'] - path to the prop name (nested can be expressed as 'a.b'), eg. 'selected'
 * @param {string} [propsCfg.key.linkName='*name*Link'] -
 * @param {string} [propsCfg.key.defaultName='default*Name*'] - path to the default prop value, eg. 'defaultSelected'
 * @param {string} [propsCfg.key.changeHandlerName='on*Name*Change'] - path to the default change event handler, eg. 'onSelectionChange'
 * @param {any|function} [propsCfg.key.defaultValue=''] - what is the master default value if no default value can be found? eg. []
 * @param {boolean} [propsCfg.key.enforceHandler=true] - whether to detect absence of changeHandler when this prop is controlled? If true an error will be logged
 * @return {WiredComponent} wired component, with all props defined in *propsCfg* now accepting both controlled and uncontrolled versions of usage
 */

export function wireSet(TargetComponent, options) {
  // normalize configs so all props have a default setting
  options = _.mapValues(options, function (opt, optionName) {
    var _opt$name = opt.name,
        name = _opt$name === void 0 ? optionName : _opt$name,
        _opt$linkName = opt.linkName,
        linkName = _opt$linkName === void 0 ? "".concat(optionName, "Link") : _opt$linkName,
        _opt$defaultName = opt.defaultName,
        defaultName = _opt$defaultName === void 0 ? "default".concat(_.capitalize(optionName)) : _opt$defaultName,
        _opt$changeHandlerNam = opt.changeHandlerName,
        changeHandlerName = _opt$changeHandlerNam === void 0 ? "on".concat(_.capitalize(optionName), "Change") : _opt$changeHandlerNam,
        _opt$enforceHandler = opt.enforceHandler,
        enforceHandler = _opt$enforceHandler === void 0 ? true : _opt$enforceHandler;
    return {
      name: name,
      linkName: linkName,
      defaultName: defaultName,
      changeHandlerName: changeHandlerName,
      enforceHandler: enforceHandler,
      defaultValue: _.get(opt, 'defaultValue', '')
    };
  }); // TODO: Customs

  function getValue(_ref, props) {
    var name = _ref.name,
        linkName = _ref.linkName,
        defaultName = _ref.defaultName,
        defaultValue = _ref.defaultValue;
    return _.get(props, name, _.get(props, "".concat(linkName, ".value"), _.get(props, defaultName, _.isFunction(defaultValue) ? defaultValue(props) : defaultValue)));
  }

  function useDefaultValues(props) {
    return useMemo(function () {
      return _.mapValues(options, function (option) {
        return {
          name: name,
          controlled: _.has(props, option.name),
          value: getValue(option, props)
        };
      });
    }, [props]);
  }

  function useValues(props) {
    var defaults = useDefaultValues(props);

    var states = _.reduce(defaults, function (s, _ref2, optionName) {
      var value = _ref2.value,
          controlled = _ref2.controlled;
      return controlled ? s : _objectSpread(_objectSpread({}, s), {}, _defineProperty({}, optionName, useState(value)));
    }, {});

    return _.mapValues(defaults, function (_ref3, optionName) {
      var controlled = _ref3.controlled,
          value = _ref3.value;
      var _options$optionName = options[optionName],
          linkName = _options$optionName.linkName,
          changeHandlerName = _options$optionName.changeHandlerName,
          enforceHandler = _options$optionName.enforceHandler;

      var handleFn = _.get(props, changeHandlerName);

      return [controlled ? getValue(options[optionName], props) : states[optionName][0], !controlled ? function (nextValue) {
        var e = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

        var _states$optionName = _slicedToArray(states[optionName], 2),
            setValue = _states$optionName[1];

        setValue(nextValue);
        handleFn instanceof Function && handleFn(nextValue, _objectSpread({
          before: value
        }, e));
      } : function (nextValue, e) {
        var valueLink = _.get(props, linkName);

        var _e = _objectSpread({
          before: value
        }, e);

        if (handleFn instanceof Function) handleFn(nextValue, _e);else if (valueLink) valueLink.requestChange(nextValue, _e);else console.error(enforceHandler ? "handleChange::".concat(TargetComponent.displayName, "::").concat(propName, ". Controlled component without a '").concat(JSON.stringify(changeHandlerName), "' event prop") : "Invalid onChange Handle Function");
      }];
    });
  }

  var WireComponent = /*#__PURE__*/React.forwardRef(function (props, ref) {
    var values = useValues(props);
    return /*#__PURE__*/React.createElement(TargetComponent, _extends({
      ref: ref
    }, _.reduce(options, function (newProps, _ref4, propName) {
      var name = _ref4.name,
          changeHandlerName = _ref4.changeHandlerName;
      return _.chain(newProps).set(name, values[propName][0]).set(changeHandlerName, values[propName][1]).value();
    }, _.reduce(options, function (omits, _ref5) {
      var name = _ref5.name,
          linkName = _ref5.linkName,
          defaultName = _ref5.defaultName,
          changeHandlerName = _ref5.changeHandlerName;
      return _.omit(omits, name, linkName, defaultName, changeHandlerName);
    }, props))));
  });
  WireComponent.propTypes = TargetComponent.propTypes || {};
  return WireComponent;
}
/**
 * Decorate given Component for specified prop name
 * @param {component} Component - which component to apply wiring to?
 * @param {string} propName - name of the prop to wire
 * @param {any|function} [defaultValue=''] - master default value if this prop has no default value
 * @param {string} [changeHandlerName='onChange'] - name of the change handler for this prop
 * @return {WiredComponent} wired component, with prop now accepting both controlled and uncontrolled versions of usage
 */

export function wire(WireComponent, propName) {
  var defaultValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  var changeHandlerName = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'onChange';
  return wireSet(WireComponent, _defineProperty({}, propName, {
    defaultValue: defaultValue,
    changeHandlerName: changeHandlerName
  }));
}
/**
 * Decorate given Component for 'value' prop
 * @param {component} Component - which component to apply wiring to?
 * @return {WiredComponent} wired component, with component now accepting value, defaultValue, and onChange props
 */

export function wireValue(WireComponent) {
  return wire(WireComponent, 'value');
}
/**
 * Decorate given Component for 'checked' prop
 * @param {component} Component - which component to apply wiring to?
 * @return {WiredComponent} wired component, with component now accepting checked, defaultChecked, and onChange props
 */

export function wireChecked(WireCheckedComponent) {
  return wire(WireCheckedComponent, 'checked', false);
}
export default {
  wireSet: wireSet,
  wireValue: wireValue,
  wireChecked: wireChecked,
  wire: wire
};